import React from "react";
import {
  FiSmile,
  FiMessageSquare,
  FiMoreHorizontal,
  FiFileText,
  FiFile,
  FiMusic,
  FiVideo,
  FiLink,
  FiDownload,
} from "react-icons/fi";
import { downloadFile } from "../../../APIServices/GoApiServices/chatService";

const MessageItem = ({ message, isGrouped, onReply, setSelectedProfile, chatToken }) => {
  const getAttachmentIcon = (type) => {
    switch (type) {
      case "pdf":
        return <FiFileText />;
      case "doc":
        return <FiFile />;
      case "audio":
        return <FiMusic />;
      case "video":
        return <FiVideo />;
      case "url":
        return <FiLink />;
      default:
        return <FiFile />;
    }
  };

  const formatBytes = (bytes) => {
    const n = Number(bytes || 0);
    if (!n) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.min(Math.floor(Math.log(n) / Math.log(k)), sizes.length - 1);
    const value = n / Math.pow(k, i);
    return `${value.toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
  };

  const inferType = (name, mime) => {
    const n = (name || "").toLowerCase();
    const m = (mime || "").toLowerCase();

    if (m.includes("pdf") || n.endsWith(".pdf")) return "pdf";
    if (m.includes("word") || n.endsWith(".doc") || n.endsWith(".docx") || n.endsWith(".rtf"))
      return "doc";
    if (m.startsWith("audio/") || /\.(mp3|wav|aac|ogg|m4a)$/i.test(n)) return "audio";
    if (m.startsWith("video/") || /\.(mp4|mkv|webm|mov|avi)$/i.test(n)) return "video";
    if (m.startsWith("text/") || /\.(txt|csv|json|xml)$/i.test(n)) return "doc";
    return "file";
  };

  const doDownload = async (fileId, fallbackName) => {
    if (!fileId) return;
    if (!chatToken) {
      alert("Missing chat token");
      return;
    }
    try {
      const { blob, filename } = await downloadFile(fileId, chatToken);
      const name = filename || fallbackName || `file_${fileId}`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Download failed");
    }
  };

  const rawFiles = Array.isArray(message?.raw?.files) ? message.raw.files : [];
  const attachments = Array.isArray(message?.attachments) ? message.attachments : [];

  const mergedAttachments = (() => {
    const out = [];
    const seen = new Set();

    for (const att of attachments) {
      const key = att?.id != null ? String(att.id) : null;
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      out.push(att);
    }

    for (const f of rawFiles) {
      const key = f?.id != null ? String(f.id) : null;
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);

      out.push({
        id: f?.id,
        type: inferType(f?.name, f?.mime),
        name: f?.name,
        title: f?.name,
        size:
          typeof f?.size === "number" || typeof f?.size === "string"
            ? formatBytes(f.size)
            : undefined,
        mime: f?.mime,
        _isRawFile: true,
      });
    }

    return out;
  })();

  return (
    <div className={`message-row ${isGrouped ? "grouped" : ""}`}>
      {!isGrouped && <div className="avatar">{message.avatar}</div>}
      {isGrouped &&
        <div style={{ marginTop: 2 }} className="time">{message.time}</div>
      }

      <div className="message-body">
        {!isGrouped && (
          <div className="message-header">
            <strong>{message.user}</strong>
            <span className="time">{message.time}</span>
          </div>
        )}
        <div style={{ lineHeight: 1 }}>
          <span style={{ fontSize: 12 }}>
            <div
              dangerouslySetInnerHTML={{ __html: message.text }}
            />
          </span>
        </div>
        <div
          onClick={() => {
            onReply(message);
            setSelectedProfile("");
          }}
        >
          {mergedAttachments?.length > 0 && (
            <div className="attachment-list">
              {mergedAttachments.map((att) => {
                const title = att.name || att.title;

                return (
                  <div
                    key={att.id || `${title}-${Math.random()}`}
                    className="attachment-item"
                    title={title}
                    onClick={(e) => e.stopPropagation()}
                    role="button"
                  >
                    <span className="attachment-icon">{getAttachmentIcon(att.type)}</span>

                    {title}

                    {att.size && <span className="attachment-size">{att.size}</span>}

                    {att.id && (
                      <span
                        style={{ marginLeft: 8, display: "inline-flex" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          doDownload(att.id, title);
                        }}
                        title="Download"
                      >
                        <FiDownload />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {message.repliesCount > 0 && (
            <div className="thread-info">
              <FiMessageSquare /> {message.repliesCount} replies
              <span className="view-thread">View thread</span>
            </div>
          )}
        </div>
      </div>

      <div className="message-actions">
        <FiSmile />
        <FiMessageSquare onClick={() => onReply(message)} />
        <FiMoreHorizontal />
      </div>
    </div>
  );
};

export default MessageItem;
